import { styled } from "goober";
import { useRef, useState, type ReactNode } from "react";
import { useScrollFade } from "../hooks/useScrollFade";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import {
  TooltipLabel,
  TooltipRow,
  TooltipTitle,
  TooltipValue,
} from "./CursorTooltip";
import { GhostPill } from "./GhostPill";
import { HelpTooltip } from "./HelpTooltip";
import { Text } from "./Text";
import { setLastTooltipCloseTime, Tooltip } from "./Tooltip";

export const defaultFormatValue = (n: number) => String(n);

export type StatusSegment = {
  key: string;
  count: number;
  color: string;
  // A treemap tile’s background; when unset, the treemap tints `color`.
  fill?: string;
  // A treemap tile’s hover ring; when unset, it uses `color`.
  ring?: string;
  label: string;
  items?: ReadonlyArray<string>;
  details?: ReadonlyArray<{ label: string; value: string }>;
  // Optional caveat surfaced via an info icon next to the segment's label.
  note?: string;
  // For budget-style donuts: the arc is sized by `capacity` rather than `count`,
  // with the `count`/`capacity` portion drawn solid and the remainder faded.
  capacity?: number;
  // Solid color for the remaining `capacity - count` track; when unset the
  // remainder falls back to a semi-transparent `color`.
  trackColor?: string;
  caption?: string;
  hideFromLegend?: boolean;
};

type Props = {
  title: string;
  titleInfo?: ReactNode;
  total: number;
  segments: ReadonlyArray<StatusSegment>;
  // When set, the bar renders these individual segments while the legend keeps
  // summarizing `segments` — used for a per-item bar with an aggregate key.
  barSegments?: ReadonlyArray<StatusSegment>;
  onSegmentHover?: (segment: StatusSegment | null) => void;
  onSegmentClick?: (segment: StatusSegment) => void;
  activeKey?: string | null;
  formatValue?: (value: number) => string;
  wrapLegend?: boolean;
  showLegendValue?: boolean;
  embedded?: boolean;
  accessory?: ReactNode;
  placeholder?: boolean;
};

const Outer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 32px;
  min-height: 0;
  min-width: 0;
  height: 160px;
`;

const FadedHeader = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: 0.4;
`;

const Header = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HeaderRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
`;

const Bar = styled("div")`
  display: flex;
  align-items: center;
  height: 10px;
  gap: 2px;
`;

const Segment = styled("div")`
  position: relative;
  height: 4px;
  border-radius: 999px;
  min-width: 4px;
  cursor: default;
  transition:
    flex var(--motion-base) var(--ease-out),
    height var(--motion-fast) var(--ease-out),
    filter var(--motion-fast) var(--ease-out),
    opacity var(--motion-fast) var(--ease-out);

  &::before {
    content: "";
    position: absolute;
    top: -10px;
    bottom: -10px;
    left: 0;
    right: 0;
  }

  &:hover,
  &[data-active="true"] {
    height: 6px;
    filter: brightness(1.1);
  }
`;

const SegmentPopover = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  box-shadow: var(--elevation-low);
  padding: 6px 8px;
  z-index: ${zIndexes.tooltip};
  max-width: 300px;
  width: max-content;
  pointer-events: none;
  text-align: left;
  font-size: 10px;
  bottom: anchor(top);
  left: anchor(left);
  translate: 0 -8px;
  position-try-fallbacks: flip-inline;
`;

const LegendRow = styled("div")`
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  min-width: 0;
  overflow: hidden;

  &[data-scrollable="true"] {
    max-height: 76px;
    overflow-y: auto;
    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent,
      black var(--fade-top, 0px),
      black calc(100% - var(--fade-bottom, 0px)),
      transparent
    );
    mask-image: linear-gradient(
      to bottom,
      transparent,
      black var(--fade-top, 0px),
      black calc(100% - var(--fade-bottom, 0px)),
      transparent
    );
  }

  &[data-faded="true"] {
    opacity: 0.4;
  }
`;

const LegendItem = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  transition: opacity var(--motion-fast) var(--ease-out);
`;

const LegendDot = styled("div")`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const TooltipList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  max-height: 180px;
  overflow-y: auto;
`;

const TooltipItem = styled("div")`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const TooltipMore = styled("div")`
  opacity: 0.6;
  padding-top: 2px;
`;

const TOOLTIP_MAX_ITEMS = 8;

function TooltipItems({ items }: { items: ReadonlyArray<string> }) {
  const shown = items.slice(0, TOOLTIP_MAX_ITEMS);
  const extra = items.length - shown.length;
  return (
    <>
      {shown.map((item) => (
        <TooltipItem key={item}>{item}</TooltipItem>
      ))}
      {extra > 0 && <TooltipMore>+{extra} more</TooltipMore>}
    </>
  );
}

function SegmentWithTooltip({
  segment,
  flexGrow,
  active,
  onHover,
  onClick,
  formatValue,
  showValue,
  dimmed,
}: {
  segment: StatusSegment;
  flexGrow: number;
  active: boolean;
  onHover: (segment: StatusSegment | null) => void;
  onClick?: (segment: StatusSegment) => void;
  formatValue: (value: number) => string;
  showValue: boolean;
  dimmed: boolean;
}) {
  return (
    <SegmentPopover
      isOpen={active}
      trigger={({ style }) => (
        <Segment
          data-active={active ? "true" : undefined}
          onMouseEnter={() => onHover(segment)}
          onMouseLeave={() => onHover(null)}
          onClick={onClick ? () => onClick(segment) : undefined}
          style={{
            ...style,
            flex: flexGrow,
            background: segment.color,
            cursor: onClick ? "pointer" : undefined,
            opacity: dimmed ? 0.4 : 1,
          }}
        />
      )}
    >
      <TooltipList>
        <TooltipTitle>
          {showValue
            ? `${formatValue(segment.count)} ${segment.label}`
            : segment.label}
        </TooltipTitle>
        {segment.details ? (
          segment.details.map((d) => (
            <TooltipRow key={d.label}>
              <TooltipLabel>{d.label}</TooltipLabel>
              <TooltipValue>{d.value}</TooltipValue>
            </TooltipRow>
          ))
        ) : (
          <TooltipItems items={segment.items ?? []} />
        )}
      </TooltipList>
    </SegmentPopover>
  );
}

function LegendTooltip({
  items,
  children,
}: {
  items?: ReadonlyArray<string>;
  children: ReactNode;
}) {
  if (!items || items.length === 0) return children;
  return (
    <Tooltip
      content={
        <TooltipList>
          <TooltipItems items={items} />
        </TooltipList>
      }
    >
      {children}
    </Tooltip>
  );
}

function ghostWeights(seed: string): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  const count = 4 + (Math.abs(h) % 3);
  const weights: number[] = [];
  for (let i = 0; i < count; i++) {
    h = (h * 1103515245 + 12345) | 0;
    weights.push(1 + (Math.abs(h) % 5));
  }
  return weights;
}

export function StatusBreakdownChart({
  title,
  titleInfo,
  total,
  segments,
  barSegments,
  onSegmentHover,
  onSegmentClick,
  activeKey = null,
  formatValue = defaultFormatValue,
  wrapLegend = false,
  showLegendValue = true,
  embedded = false,
  accessory,
  placeholder,
}: Props) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef: legendRef });
  const isEmpty = placeholder || total === 0;
  const mutedColor = "var(--alpha8)";
  const HeaderEl = isEmpty ? FadedHeader : Header;
  const legendSegments = placeholder
    ? [
        { key: "ghost-1", count: 5, color: mutedColor, label: "" },
        { key: "ghost-2", count: 3, color: mutedColor, label: "" },
        { key: "ghost-3", count: 2, color: mutedColor, label: "" },
        { key: "ghost-4", count: 1, color: mutedColor, label: "" },
      ]
    : segments;
  const emphasisKey = hoveredKey ?? activeKey;
  const handleHover = (segment: StatusSegment | null) => {
    if (!segment && hoveredKey != null) setLastTooltipCloseTime();
    setHoveredKey(segment ? segment.key : null);
    onSegmentHover?.(segment);
  };
  return (
    <Outer
      aria-hidden={placeholder || undefined}
      style={
        embedded
          ? { padding: 0, height: "auto", minHeight: 0 }
          : wrapLegend
            ? { height: "auto", minHeight: 160 }
            : undefined
      }
    >
      {!embedded && (
        <HeaderRow>
          <HeaderEl>
            <TitleRow>
              <Text variant="text-small" color="var(--text-secondary)">
                {title}
              </Text>
              {titleInfo != null && <HelpTooltip content={titleInfo} />}
            </TitleRow>
            <Text
              variant="text-regular-md"
              color={isEmpty ? "var(--text-secondary)" : "var(--text-primary)"}
            >
              {isEmpty ? "–" : formatValue(total)}
            </Text>
          </HeaderEl>
          {accessory}
        </HeaderRow>
      )}
      <Body>
        <Bar>
          {!isEmpty &&
            (barSegments ?? segments).map((s) =>
              s.count > 0 ? (
                <SegmentWithTooltip
                  key={s.key}
                  segment={s}
                  flexGrow={total > 0 ? s.count / total : s.count}
                  active={hoveredKey === s.key}
                  onHover={handleHover}
                  onClick={onSegmentClick}
                  formatValue={formatValue}
                  showValue={barSegments ? false : showLegendValue}
                  dimmed={
                    !barSegments && emphasisKey != null && emphasisKey !== s.key
                  }
                />
              ) : null
            )}
          {isEmpty &&
            ghostWeights(title).map((w, i) => (
              <Segment
                // oxlint-disable-next-line react/no-array-index-key -- ghost segments are decorative
                key={i}
                style={{
                  flex: w,
                  background: mutedColor,
                  pointerEvents: "none",
                }}
              />
            ))}
        </Bar>
        <LegendRow
          ref={legendRef}
          data-faded={isEmpty ? "true" : undefined}
          data-scrollable={wrapLegend && !isEmpty ? "true" : undefined}
          style={wrapLegend ? { flexWrap: "wrap", rowGap: 6 } : undefined}
        >
          {legendSegments.map((s) =>
            placeholder ? (
              <LegendItem key={s.key}>
                <LegendDot style={{ background: mutedColor }} />
                <GhostPill style={{ width: 16 + s.count * 8 }} />
              </LegendItem>
            ) : (
              <LegendTooltip
                key={s.key}
                items={barSegments ? s.items : undefined}
              >
                <LegendItem
                  onMouseEnter={isEmpty ? undefined : () => handleHover(s)}
                  onMouseLeave={isEmpty ? undefined : () => handleHover(null)}
                  onClick={
                    isEmpty || barSegments || !onSegmentClick
                      ? undefined
                      : () => onSegmentClick(s)
                  }
                  style={{
                    // Standard cursor on hover; only a clickable row
                    // (onSegmentClick) gets a pointer. Never the text I-beam.
                    cursor:
                      !isEmpty && !barSegments && onSegmentClick
                        ? "pointer"
                        : "default",
                    opacity:
                      !barSegments &&
                      emphasisKey != null &&
                      emphasisKey !== s.key
                        ? 0.4
                        : 1,
                  }}
                >
                  <LegendDot
                    style={{ background: isEmpty ? mutedColor : s.color }}
                  />
                  <Text variant="text-small" color="var(--text-secondary)">
                    {showLegendValue
                      ? `${formatValue(s.count)} ${s.label}`
                      : s.label}
                  </Text>
                </LegendItem>
              </LegendTooltip>
            )
          )}
        </LegendRow>
      </Body>
    </Outer>
  );
}
